/**
 * DM-8485: the sheet round trip above a website-lead table. "Export
 * recipients" downloads the filtered list as the CSV the founders mail merge
 * from (lead_id first, so the sheet comes back matchable); "Import sent log"
 * takes the stamped sheet back and appends one email_sent per row. The export
 * is a bulk read of personal data and the import a write, so auditors see
 * neither.
 */

import { useMemo, useState } from 'react';
import { Download, Upload } from 'lucide-react';
import { useAuth } from '../../components/AuthProvider';
import {
  IMPORT_BATCH_SIZE,
  adminOpsApi,
  type EarlyAccessLeadExportParams,
  type FoundingPartnerRequestExportParams,
} from '../../api/adminOps';
import { useToast } from '../../components/ui';
import { useImportWebsiteLeadOutreach } from '../../hooks/useAdminOps';
import {
  IMPORT_MAX_ROWS,
  OUTREACH_ACTOR_MAX_LENGTH,
  countNoun,
  exportResultMessage,
  importResultLine,
  parseSentLog,
  sentLogPreviewLine,
  triggerCsvDownload,
} from '../websiteLeads.logic';

type OutreachToolsProps =
  | { leadType: 'early_access'; params: EarlyAccessLeadExportParams }
  | { leadType: 'founding_partner'; params: FoundingPartnerRequestExportParams };

/** How many parse errors the preview lists before summarising the rest. */
const PREVIEW_ERRORS = 3;

export default function OutreachTools(props: OutreachToolsProps) {
  const { leadType } = props;
  // EXPORT_WEBSITE_LEADS and IMPORT_WEBSITE_LEAD_OUTREACH need admin or operator.
  const { hasRole, user } = useAuth();
  const toast = useToast();
  const importMutation = useImportWebsiteLeadOutreach();
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [sheet, setSheet] = useState('');
  const [actorLabel, setActorLabel] = useState(user?.email ?? '');
  const [result, setResult] = useState<string | null>(null);
  const parsed = useMemo(() => parseSentLog(sheet, leadType), [sheet, leadType]);
  const tooMany = parsed.rows.length > IMPORT_MAX_ROWS;

  if (!hasRole('admin', 'admin-operator')) return null;

  const exportRecipients = async () => {
    setExporting(true);
    try {
      const response =
        props.leadType === 'early_access'
          ? await adminOpsApi.exportEarlyAccessLeads(props.params)
          : await adminOpsApi.exportFoundingPartnerRequests(props.params);
      triggerCsvDownload(response.data.filename, response.data.csv);
      const message = exportResultMessage(response.data);
      toast[message.kind](message.message);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const submitImport = () => {
    const trimmedActor = actorLabel.trim();
    importMutation.mutate(
      {
        lead_type: leadType,
        ...(trimmedActor ? { actor_label: trimmedActor } : {}),
        rows: parsed.rows,
      },
      {
        onSuccess: response => {
          setResult(importResultLine(response.data));
          setSheet('');
        },
      }
    );
  };

  const idPrefix = `${leadType}-sent-log`;

  return (
    <div className="border-t border-gray-border pt-3 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-text mr-2">Mail merge:</span>
        <button
          onClick={exportRecipients}
          disabled={exporting}
          className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {exporting ? 'Exporting…' : 'Export recipients'}
        </button>
        <button
          onClick={() => setImportOpen(open => !open)}
          aria-expanded={importOpen}
          className="btn-secondary inline-flex items-center gap-2"
        >
          <Upload className="w-4 h-4" />
          Import sent log
        </button>
        <p className="text-xs text-gray-text basis-full">
          Export the filtered list for the Google Sheet (lead_id stays in the first column); paste
          the stamped sheet back to log what was sent. Leads marked Do not contact are never
          exported.
        </p>
      </div>

      {importOpen && (
        <div className="space-y-2" role="region" aria-label="Import sent log">
          <div>
            <label className="label" htmlFor={idPrefix}>
              Stamped sheet
            </label>
            <textarea
              id={idPrefix}
              className="input w-full min-h-[120px] font-mono text-xs"
              placeholder={'lead_id\temail\tsent_at\ttouch\tsubject'}
              value={sheet}
              onChange={event => {
                setSheet(event.target.value);
                setResult(null);
              }}
            />
            <p className="text-xs text-gray-text mt-1">
              Paste the sheet with its header row (CSV or tab separated). Each row needs lead_id or
              email and a sent timestamp; up to {IMPORT_MAX_ROWS} rows per import, sent in batches
              of {IMPORT_BATCH_SIZE}. Rows already logged are skipped, so pasting the same sheet
              twice is safe.
            </p>
          </div>
          <div>
            <label className="label" htmlFor={`${idPrefix}-actor`}>
              Sender
            </label>
            <input
              id={`${idPrefix}-actor`}
              className="input"
              maxLength={OUTREACH_ACTOR_MAX_LENGTH}
              placeholder="Who sent these emails"
              value={actorLabel}
              onChange={event => setActorLabel(event.target.value)}
            />
          </div>
          <p className="text-sm text-white">{sentLogPreviewLine(parsed)}</p>
          {parsed.errors.length > 0 && (
            <ul className="text-xs text-red-400 space-y-0.5">
              {parsed.errors.slice(0, PREVIEW_ERRORS).map(error => (
                <li key={`${error.line}-${error.message}`}>
                  Line {error.line}: {error.message}
                </li>
              ))}
              {parsed.errors.length > PREVIEW_ERRORS && (
                <li>…and {parsed.errors.length - PREVIEW_ERRORS} more</li>
              )}
            </ul>
          )}
          {tooMany && (
            <p className="text-xs text-red-400">
              Up to {IMPORT_MAX_ROWS} rows per import; split the sheet.
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={submitImport}
              disabled={parsed.rows.length === 0 || tooMany || importMutation.isPending}
              className="btn-primary disabled:opacity-50"
            >
              {importMutation.isPending
                ? 'Importing…'
                : `Import ${countNoun(parsed.rows.length, 'row')}`}
            </button>
            <button onClick={() => setImportOpen(false)} className="btn-secondary">
              Close
            </button>
          </div>
          {result && <p className="text-sm text-green-400">{result}</p>}
        </div>
      )}
    </div>
  );
}
